"use client";

import { useEffect, useRef } from "react";

import { FIFA_ASSET_VERSION } from "@/constants/vars.constants";
import { gsap, ScrollTrigger } from "@/lib/gsap";

import { initHeroBackgroundAnimation } from "./HeroBackground.animation";
import styles from "./HeroBackground.module.scss";

export default function HeroBackground() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const blurRef = useRef<HTMLDivElement>(null);
  const reasonBgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cleanupAnimation: (() => void) | undefined;

    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }

    window.scrollTo(0, 0);

    const timeout = setTimeout(() => {
      if (
        !sectionRef.current ||
        !videoRef.current ||
        !overlayRef.current ||
        !blurRef.current ||
        !reasonBgRef.current
      ) {
        return;
      }

      gsap.set(videoRef.current, {
        scale: 1,
        clearProps: "filter",
      });

      gsap.set(overlayRef.current, {
        backgroundColor: "rgba(0,0,0,0)",
      });

      gsap.set(blurRef.current, {
        backdropFilter: "blur(0px)",
        backgroundColor: "rgba(0,0,0,0)",
      });

      gsap.set(reasonBgRef.current, {
        opacity: 0,
      });

      videoRef.current.play();

      cleanupAnimation = initHeroBackgroundAnimation({
        triggerRef: sectionRef.current,
        videoRef: videoRef.current,
        overlayRef: overlayRef.current,
        blurRef: blurRef.current,
        reasonBgRef: reasonBgRef.current,
      });

      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(timeout);

      cleanupAnimation?.();

      ScrollTrigger.getAll().forEach((trigger) => {
        trigger.kill();
      });
    };
  }, []);

  return (
    <section
      className={styles.heroBg}
      ref={sectionRef}
    >
      <div className={styles.stickyMedia}>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className={styles.video}
        >
          <source
            media="(max-width:768px)"
            src={`/fifa/FIFA_KV_MB.mp4?v=${FIFA_ASSET_VERSION}`}
            type="video/mp4"
          />

          <source
            media="(min-width:769px)"
            src={`/fifa/FIFA_KV_Web.mp4?v=${FIFA_ASSET_VERSION}`}
            type="video/mp4"
          />
        </video>

        {/* Dark Overlay */}
        <div
          className={styles.overlay}
          ref={overlayRef}
        />

        {/* Blur Layer */}
        <div
          className={styles.blurLayer}
          ref={blurRef}
        />
        <div
          className={styles.reasonBg}
          ref={reasonBgRef}
        />
      </div>
    </section>
  );
}